import { checkSession } from "@features/auth";
import { useAsyncMutation } from "@hooks/useAsyncMutation";
import { useI18nContext } from "@i18n/i18n-react";
import { preloadQuery } from "@relay";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { usePreloadedQuery } from "react-relay";
import { graphql } from "relay-runtime";
import { toast } from "sonner";
import z from "zod";
import { OpenDoorPage } from "../$hubMetaName/pick-up/-OpenDoorPage";
import type { pickupOneDeliveryQuery } from "./__generated__/pickupOneDeliveryQuery.graphql";
import type { pickupOneDeliveryCollectMutation } from "./__generated__/pickupOneDeliveryCollectMutation.graphql";

const query = graphql`
	query pickupOneDeliveryQuery($id: UUID!) {
		bundle(id: $id) {
			id
			hub @required(action: THROW) {
				id
				meta {
					id
					name
				}
			}
		}
	}
`;

const collectMutation = graphql`
	mutation pickupOneDeliveryCollectMutation($input: BundleCollectInput!) {
		bundleCollect(input: $input) {
			id
		}
	}
`;

const searchSchema = z.object({ bundleId: z.string().uuid(), reservationId: z.string().uuid() });

export const Route = createFileRoute("/one-delivery/pickup")({
	beforeLoad: () => {
		document.title = "bacehub | One Delivery";
	},
	component: RouteComponent,
	validateSearch: zodValidator(searchSchema),
	loaderDeps: ({ search: { bundleId } }) => ({ bundleId }),
	loader: async ({ context, deps }) => {
		if (!(await checkSession())) throw redirect({ to: "/auth", search: { redirectToPath: "/one-delivery", show: "sign-in" } });
		return preloadQuery<pickupOneDeliveryQuery>(context.relayEnvironment, query, { id: deps.bundleId });
	},
});

function RouteComponent() {
	const i18n = useI18nContext().LL["new-ui"];
	const navigate = useNavigate();
	const { bundleId, reservationId } = Route.useSearch();
	const { bundle } = usePreloadedQuery<pickupOneDeliveryQuery>(query, Route.useLoaderData());
	const [collect, isCollecting] = useAsyncMutation<pickupOneDeliveryCollectMutation>(collectMutation);

	return (
		<OpenDoorPage
			hubId={bundle.hub.id}
			hubName={bundle.hub.meta?.name ?? bundle.hub.id}
			// The recipient must scan the destination hub; any of its QR codes (general or side) is accepted.
			side={undefined}
			isLoading={isCollecting}
			onOpenDoor={async () => {
				if (isCollecting) return;
				try {
					const response = await collect({ variables: { input: { bundleId, reservationId } } });
					navigate({ to: "/one-delivery/opening", search: { bundleId, unlockRequestId: response.bundleCollect.id } });
				} catch (error) {
					console.error(error);
					toast.error(i18n.OD_OPEN_DOOR_FAILED());
				}
			}}
		/>
	);
}
